<template>
  <div>
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 class="mb-1">Управление Предиктами</h5>
          <p class="text-muted mb-0">Настраивайте предикты и подводите итоги.</p>
        </div>
        <b-button variant="primary" @click="showCreateModal">
          <i class="fa fa-plus mr-1"></i> Создать предикт
        </b-button>
      </div>

      <div class="form-group mb-4">
        <label class="font-weight-bold">Выберите трансляцию</label>
        <b-form-select v-model="selectedWatchId" :options="watchOptions" @change="onWatchChange"></b-form-select>
      </div>

      <div v-if="predictionTeams.length" class="mb-4">
        <h6>Команды предикта</h6>
        <b-table
          responsive
          :items="predictionTeams"
          :fields="predictionTeamFields"
          :busy="isTeamsLoading"
          small
          show-empty
          empty-text="Нет команд"
        ></b-table>
      </div>

      <b-table
        responsive
        :items="predictions"
        :fields="fields"
        :busy="isLoading"
        show-empty
        empty-text="Нет предиктов"
      >
        <template #cell(prize_fund)="row">
          {{ row.item.prize_fund || '—' }}
        </template>
        <template #cell(duration_minutes)="row">
          {{ row.item.duration_minutes ? row.item.duration_minutes + ' мин' : '—' }}
        </template>
        <template #cell(actions)="row">
          <div class="d-flex justify-content-end align-items-center">
            <b-button
              v-if="row.item.status === 'draft'"
              size="sm"
              variant="success"
              @click="publishPrediction(row.item.id)"
              class="mr-2"
              title="Опубликовать"
            >
              <i class="fa fa-play"></i>
            </b-button>
            <b-button
              v-if="row.item.status === 'active'"
              size="sm"
              variant="warning"
              @click="closePrediction(row.item.id)"
              class="mr-2"
              title="Закрыть прием"
            >
              <i class="fa fa-stop"></i>
            </b-button>
            <b-button
              v-if="row.item.status === 'closed'"
              size="sm"
              variant="info"
              @click="openResolveModal(row.item.id)"
              class="mr-2"
              title="Подвести итоги"
            >
              <i class="fa fa-check"></i>
            </b-button>
            <b-button
                v-if="row.item.status === 'draft'"
              size="sm"
              variant="secondary"
              @click="showEditModal(row.item)"
              class="mr-2"
              title="Редактировать"
            >
              <i class="fa fa-pencil"></i>
            </b-button>
            <b-button
              size="sm"
              variant="primary"
              @click="showDetail(row.item)"
              class="mr-2"
              title="Подробнее"
            >
              <i class="fa fa-eye"></i>
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <PredictionCreateModal
      :visible="showForm"
      :is-edit="isEdit"
      :form="form"
      :stage-options="stageOptions"
      :group-options="groupOptions"
      @close="showForm = false"
      @save="isEdit ? updatePrediction() : savePrediction()"
      @stage-change="onStageChange"
      @update:name="form.name = $event"
      @update:description="form.description = $event"
      @update:group_id="form.group_id = $event"
      @update:prize_fund="form.prize_fund = $event"
      @update:duration_minutes="form.duration_minutes = $event"
    />

    <PredictionDetailModal
      :visible="showDetailModal"
      :prediction="detailPrediction"
      :teams="detailTeams"
      @close="showDetailModal = false"
      @close-prediction="closeFromDetail"
      @resolve-prediction="resolveFromDetail"
    />

    <PredictionResolveModal
      :visible="showResolveModal"
      :teams="resolveTeams"
      @close="showResolveModal = false"
      @resolve="resolvePrediction"
    />
  </div>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import api from '@/api/api';
import PredictionCreateModal from '@/components/Watch/Predicts/CreateModal';
import PredictionDetailModal from '@/components/Watch/Predicts/DetailModal';
import PredictionResolveModal from '@/components/Watch/Predicts/ResolveModal';

export default {
  name: "WatchPredicts",
  components: { PredictionCreateModal, PredictionDetailModal, PredictionResolveModal },
  data() {
    return {
      selectedWatchId: null,
      predictions: [],
      isLoading: false,
      predictionTeams: [],
      isTeamsLoading: false,
      showForm: false,
      isEdit: false,
      editPredictionId: null,
      editTournamentId: null,
      stages: [],
      groups: [],
      form: {
        name: '',
        description: '',
        stage_id: null,
        group_id: null,
        prize_fund: '',
        duration_minutes: null,
      },
      fields: [
        {key: 'id', label: 'ID'},
        {key: 'prize_fund', label: 'Призовой фонд'},
        {key: 'duration_minutes', label: 'Длительность'},
        {key: 'actions', label: 'Действия', class: 'text-right'}
      ],
      predictionTeamFields: [
        {key: 'id', label: 'ID'},
        {key: 'name', label: 'Команда'},
        {key: 'tier', label: 'Tier'},
      ],
      showDetailModal: false,
      detailPrediction: null,
      detailTeams: [],
      showResolveModal: false,
      resolvePredictionId: null,
      resolveTeams: [],
    }
  },
  computed: {
    ...mapState('moduleWatch', ['watches', 'mainBanner']),
    allWatches() {
      const list = [...this.watches];
      if (this.mainBanner && !list.some(w => w.id === this.mainBanner.id)) {
        list.unshift(this.mainBanner);
      }
      return list;
    },
    watchOptions() {
      return [
        {value: null, text: 'Выберите трансляцию'},
        ...this.allWatches.map(w => {
          const name = w.title?.ru || w.youtube_link;
          const tournament = w.tournament?.name;
          return {
            value: w.id,
            text: tournament ? `${name} (${tournament})` : name
          };
        })
      ];
    },
    selectedWatch() {
      return this.allWatches.find(w => w.id === this.selectedWatchId) || null;
    },
    stageOptions() {
      return [
        {value: null, text: 'Выберите стадию'},
        ...this.stages.map(s => ({value: s.id, text: s.name || `Стадия #${s.id}`}))
      ];
    },
    groupOptions() {
      return [
        {value: null, text: 'Выберите группу'},
        ...this.groups.map(g => ({value: g.id, text: g.name || `Группа #${g.id}`}))
      ];
    },
  },
  methods: {
    ...mapActions('moduleWatch', ['fetchWatches']),
    getStatusVariant(status) {
      const map = {draft: 'secondary', published: 'success', closed: 'warning', resolved: 'info'};
      return map[status] || 'secondary';
    },
    getStatusLabel(status) {
      const map = {draft: 'Черновик', published: 'Активен', closed: 'Закрыт', resolved: 'Завершён'};
      return map[status] || status;
    },
    async loadStages() {
      const tournamentId = this.selectedWatch?.tournament?.id;
      if (!tournamentId) {
        this.stages = [];
        this.groups = [];
        return;
      }
      try {
        const response = await api.tournamentStage.tournamentStages(tournamentId);
        this.stages = response.data.stages || response.data.data || response.data || [];
      } catch (error) {
        console.error('Error loading stages:', error);
        this.stages = [];
      }
    },
    async onStageChange(stageId) {
      this.form.stage_id = stageId;
      this.form.group_id = null;
      this.groups = [];
      if (!stageId) return;
      try {
        const response = await api.newTeams.getGroups(stageId);
        this.groups = response.data.groups || response.data.data || [];
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    },
    async showEditModal(prediction) {
      this.isEdit = true;
      this.editPredictionId = prediction.id;
      this.editTournamentId = prediction.tournament_id || prediction.tournament?.id || this.selectedWatch?.tournament?.id;
      this.form = {
        name: prediction.name || '',
        description: prediction.description || '',
        stage_id: prediction.stage_id || null,
        group_id: prediction.group_id || null,
        prize_fund: prediction.prize_fund || '',
        duration_minutes: prediction.duration_minutes || null,
      };
      await this.loadStages();
      if (this.form.stage_id) {
        await this.onStageChange(this.form.stage_id);
        this.form.group_id = prediction.group_id || null;
      }
      this.showForm = true;
    },
    async updatePrediction() {
      try {
        const params = {
          name: this.form.name,
          description: this.form.description,
          tournament_id: this.editTournamentId,
          stage_id: this.form.stage_id,
          group_id: this.form.group_id,
          prize_fund: this.form.prize_fund,
          duration_minutes: this.form.duration_minutes,
        };
        await api.prediction.update(this.editPredictionId, params);
        this.$toast.success('Предикт обновлён', {position: 'top-right', timeout: 2000});
        this.showForm = false;
        await this.refreshPrediction(this.editPredictionId);
      } catch (error) {
        console.error('Error updating prediction:', error);
        this.$toast.error('Ошибка при обновлении', {position: 'top-right'});
      }
    },
    showCreateModal() {
      if (!this.selectedWatchId) {
        this.$toast.error('Сначала выберите трансляцию', {position: 'top-right'});
        return;
      }
      if (!this.selectedWatch?.tournament?.id) {
        this.$toast.error('У трансляции нет привязки к турниру', {position: 'top-right'});
        return;
      }
      this.isEdit = false;
      this.editPredictionId = null;
      this.form = {name: '', description: '', stage_id: null, group_id: null, prize_fund: '', duration_minutes: null};
      this.loadStages();
      this.showForm = true;
    },
    async onWatchChange() {
      this.predictions = [];
      this.predictionTeams = [];
      if (!this.selectedWatchId) return;

      const watch = this.allWatches.find(w => w.id === this.selectedWatchId);
      const predictionId = watch?.prediction?.id;
      if (!predictionId) return;

      this.isLoading = true;
      try {
        const [predictionRes, teamsRes] = await Promise.all([
          api.prediction.show(predictionId),
          api.prediction.getTeams(predictionId),
        ]);
        const prediction = predictionRes.data.data || predictionRes.data;
        if (prediction) {
          this.predictions = [prediction];
        }
        this.predictionTeams = teamsRes.data.data || teamsRes.data || [];
      } catch (error) {
        console.error('Error loading prediction:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async savePrediction() {
      if (!this.form.stage_id || !this.form.group_id) return;
      try {
        const params = {
          name: this.form.name,
          description: this.form.description,
          tournament_id: this.selectedWatch.tournament?.id,
          stage_id: this.form.stage_id,
          group_id: this.form.group_id,
          prize_fund: this.form.prize_fund,
          duration_minutes: this.form.duration_minutes,
        };
        const response = await api.prediction.store(this.selectedWatchId, params);
        const created = response.data.data || response.data;
        if (created) {
          this.predictions.push(created);
        }
        await this.fetchWatches();
        this.$toast.success('Предикт создан', {position: 'top-right', timeout: 2000});
        this.showForm = false;
      } catch (error) {
        console.error('Error saving prediction:', error);
        this.$toast.error('Ошибка при создании', {position: 'top-right'});
      }
    },
    async refreshPrediction(id) {
      try {
        const response = await api.prediction.show(id);
        const updated = response.data.data || response.data;
        const index = this.predictions.findIndex(p => p.id === id);
        if (index !== -1 && updated) {
          this.$set(this.predictions, index, updated);
        }
      } catch (error) {
        console.error('Error refreshing prediction:', error);
      }
    },
    async publishPrediction(id) {
      try {
        await api.prediction.publish(id);
        this.$toast.success('Предикт опубликован', {position: 'top-right', timeout: 2000});
        await this.refreshPrediction(id);
      } catch (error) {
        console.error('Error publishing prediction:', error);
      }
    },
    async closePrediction(id) {
      try {
        await api.prediction.close(id);
        this.$toast.success('Прием прогнозов закрыт', {position: 'top-right', timeout: 2000});
        await this.refreshPrediction(id);
      } catch (error) {
        console.error('Error closing prediction:', error);
      }
    },
    async openResolveModal(predictionId) {
      this.resolvePredictionId = predictionId;
      this.resolveTeams = [];
      try {
        const teamsRes = await api.prediction.getTeams(predictionId);
        this.resolveTeams = teamsRes.data.data || teamsRes.data || [];
      } catch (error) {
        console.error('Error loading teams for resolve:', error);
      }
      this.showResolveModal = true;
    },
    async resolvePrediction(winnerTeamId) {
      const id = this.resolvePredictionId;
      try {
        await api.prediction.resolve(id, { winner_team_id: winnerTeamId });
        this.$toast.success('Итоги подведены', {position: 'top-right', timeout: 2000});
        this.showResolveModal = false;
        await this.refreshPrediction(id);
      } catch (error) {
        console.error('Error resolving prediction:', error);
        this.$toast.error('Ошибка при подведении итогов', {position: 'top-right'});
      }
    },
    async closeFromDetail() {
      const id = this.detailPrediction?.id;
      if (!id) return;
      await this.closePrediction(id);
      this.showDetailModal = false;
    },
    resolveFromDetail() {
      const id = this.detailPrediction?.id;
      if (!id) return;
      this.showDetailModal = false;
      this.openResolveModal(id);
    },
    async showDetail(prediction) {
      this.detailPrediction = { ...prediction };
      this.detailTeams = [];
      this.showDetailModal = true;

      try {
        const predictionRes = await api.prediction.show(prediction.id);
        const fullPrediction = predictionRes.data.data || predictionRes.data;

        if (fullPrediction) {
          this.detailPrediction = { ...fullPrediction };
          this.detailTeams = fullPrediction.teams || [];
        }

        const tournamentId = this.detailPrediction.tournament_id || this.detailPrediction.tournament?.id;
        if (tournamentId) {
          const watch = this.allWatches.find(w => w.tournament?.id === tournamentId);
          const tournamentName = watch?.tournament?.name;
          if (tournamentName) {
            this.detailPrediction = { ...this.detailPrediction, tournament_name: tournamentName };
          }

          const stageId = this.detailPrediction.stage_id || this.detailPrediction.stage?.id;
          if (stageId) {
            const stagesRes = await api.tournamentStage.tournamentStages(tournamentId);
            const stages = stagesRes.data.stages || stagesRes.data.data || stagesRes.data || [];
            const stage = stages.find(s => s.id === stageId);
            if (stage) {
              this.detailPrediction = { ...this.detailPrediction, stage_name: stage.name };
            }

            const groupId = this.detailPrediction.group_id || this.detailPrediction.group?.id;
            if (groupId) {
              const groupsRes = await api.newTeams.getGroups(stageId);
              const groups = groupsRes.data.groups || groupsRes.data.data || [];
              const group = groups.find(g => g.id === groupId);
              if (group) {
                this.detailPrediction = { ...this.detailPrediction, group_name: group.name };
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching prediction details:', error);
      }
    }
  },
  created() {
    this.fetchWatches();
  }
}
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}
.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}
</style>
